// Shared reactive source for the viewer's preferred grammatical gender. Drives
// gendered Croatian copy across badges, first-person sentences, and anywhere
// `applyGender` / `pickGender` is rendered.
//
// Pinia owns the singleton state — the watcher installed in setup runs exactly
// once per app and resets to the default on sign-out so the next signed-in
// user starts from a clean baseline before their row loads.

import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

import { Gender } from '../models/User';
import { getUserPreferences } from '../services/api/userPreferencesService';
import { useUserStore } from './userStore';
import { logError } from '../utils/logger';

export const useGenderStore = defineStore('gender', () => {
    const userStore = useUserStore();
    const gender = ref<Gender>(Gender.Male);

    watch(
        () => userStore.user?.id ?? null,
        async (userId, previousUserId) => {
            if (userId == null) {
                gender.value = Gender.Male;
                return;
            }
            if (userId === previousUserId) {
                return;
            }

            try {
                const prefs = await getUserPreferences(userId);
                gender.value = prefs.preferredGender;
            } catch (error) {
                // Fall back to the default — the service already returns a
                // default-shaped row on fetch failure, so reaching this branch
                // means an unexpected exception. Don't wedge the UI; log and
                // keep masculine copy.
                gender.value = Gender.Male;
                logError(error);
            }
        },
        { immediate: true },
    );

    /**
     * Allows the Settings UI to push a new preference into the shared state
     * without waiting for a fresh fetch. Persistence is the caller's
     * responsibility — this only updates the in-memory ref.
     */
    function setGender(next: Gender) {
        gender.value = next;
    }

    return {
        gender,
        setGender,
    };
});
